import { Component, Input, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { LocalStorageService } from "angular-2-local-storage";
import { BsModalRef, BsModalService } from "ngx-bootstrap";
import { environment } from "../../../../environments/environment";
import { Country } from "../../../constant/country.constant";
import { AuthorizedApps } from "../../../enum/shared/authorized-apps.enum";
import { BackOfficeRole } from "../../../enum/shared/backoffice-role.enum";
import { getBackOfficeRoleList, getCountryList } from "../../../functions/common.function";
import { AuthorizedAppModel } from "../../../model/shared/authorized-app.model";
import { PolicyModel } from "../../../model/shared/policy.model";
import { SelectorModel } from "../../../model/shared/selector.model";
import { FirestoreService } from "../../../service/api/auth/firestore.service";

@Component({
    selector: "bo-edit-role-modal",
    templateUrl: "./edit-role-modal.component.html",
    styleUrls: ["./edit-role-modal.component.scss"]
})

export class EditRoleModalComponent implements OnInit {
    modalRef: BsModalRef;
    @ViewChild("template") template: TemplateRef<any>;

    countries = getCountryList()
        .filter(f => f.id !== Country.All);
    roles: Array<SelectorModel>;
    selectedBuilkCountryList = new Array<string>();
    selectedJubiliCountryList = new Array<string>();

    backOfficeRoleForm: FormGroup;
    countryOnBuilkForm: FormGroup;
    countryOnJubiliForm: FormGroup;
    authorizedApps = new Array<AuthorizedAppModel>();
    policies: Array<PolicyModel>;
    data: PolicyModel;

    @Input() set defaultModel(value) {
        this.data = value;
    }
    get defaultModel(): PolicyModel {
        return this.data;
    }

    constructor(
        private readonly modalService: BsModalService,
        private readonly firestoreService: FirestoreService,
        private readonly localStorageService: LocalStorageService
    ) {
        this.backOfficeRoleForm = new FormGroup(
            {
                name: new FormControl(null, [Validators.required, Validators.nullValidator]),
                email: new FormControl(null, [Validators.required, Validators.nullValidator]),
                country: new FormControl(null, [Validators.required, Validators.nullValidator]),
                role: new FormControl(null, [Validators.required, Validators.nullValidator]),
                countryOnBuilkForm: new FormControl(null, null),
                countryOnJubiliForm: new FormControl(null, null),
                countryOnIntakeForm: new FormControl(null, null)
            });
    }

    ngOnInit(): void {
        this.firestoreService.getDocument(environment.firebaseConfig.policyCollection)
            .subscribe(data => {
                this.policies = data.map<any>(e => {
                    return {
                        id: e.payload.doc.id,
                        ...e.payload.doc.data()
                    };
                });

                const userPolicy = this.policies.find(f => f.userEmail === this.localStorageService.get<string>("userEmail"));
                this.policies.forEach(element => {
                    element.currentUserRole = userPolicy.userRole;
                });

                if (userPolicy.userRole === BackOfficeRole.SuperUser) {
                    this.roles = getBackOfficeRoleList();
                } else {
                    this.roles = getBackOfficeRoleList()
                        .filter(f => f.id !== String(BackOfficeRole.SuperUser));
                }
            });
    }

    openModal(): void {
        this.modalRef = this.modalService.show(this.template, {
            class: "modal-lg",
            backdrop: "static",
            ignoreBackdropClick: true
        });

        // Initial data
        this.backOfficeRoleForm.controls["name"].setValue(this.data.userFullName);
        this.backOfficeRoleForm.controls["email"].setValue(this.data.userEmail);
        this.backOfficeRoleForm.controls["country"].setValue(this.data.userCountry);
        this.backOfficeRoleForm.controls["role"].setValue(String(this.data.userRole));

        this.data.authorizedApps.forEach(element => {
            if (element.appName === AuthorizedApps.Builk) {
                this.backOfficeRoleForm.controls["countryOnBuilkForm"].setValue(element.country);
            } else if (element.appName === AuthorizedApps.Jubili) {
                this.backOfficeRoleForm.controls["countryOnJubiliForm"].setValue(element.country);
            } else if (element.appName === AuthorizedApps.Intake) {
                this.backOfficeRoleForm.controls["countryOnIntakeForm"].setValue(element.country);
            }
        });
    }

    onCancel(): any {
        this.modalRef.hide();
    }

    async onUpdateRole(): Promise<void> {
        // Clear object
        this.authorizedApps = [];
        // Builk
        const authorizedBuilk = {
            appName: AuthorizedApps.Builk,
            country: this.backOfficeRoleForm.controls["countryOnBuilkForm"].value
        };
        // Jubili
        const authorizedJubili = {
            appName: AuthorizedApps.Jubili,
            country: this.backOfficeRoleForm.controls["countryOnJubiliForm"].value
        };

        // Intake
        const authorizedIntake = {
            appName: AuthorizedApps.Intake,
            country: this.backOfficeRoleForm.controls["countryOnIntakeForm"].value
        };

        const countryOnBuilkFormArray = (this.backOfficeRoleForm.controls["countryOnBuilkForm"].value as Array<string>) === null ?
            [] : (this.backOfficeRoleForm.controls["countryOnBuilkForm"].value as Array<string>);
        const countryOnJubiliFormArray = (this.backOfficeRoleForm.controls["countryOnJubiliForm"].value as Array<string>) === null ?
            [] : (this.backOfficeRoleForm.controls["countryOnJubiliForm"].value as Array<string>);
        const countryOnIntakeFormArray = (this.backOfficeRoleForm.controls["countryOnIntakeForm"].value as Array<string>) === null ?
            [] : (this.backOfficeRoleForm.controls["countryOnIntakeForm"].value as Array<string>);

        countryOnBuilkFormArray.length > 0 ? this.authorizedApps.push(authorizedBuilk) : this.authorizedApps.push();
        countryOnJubiliFormArray.length > 0 ? this.authorizedApps.push(authorizedJubili) : this.authorizedApps.push();
        countryOnIntakeFormArray.length > 0 ? this.authorizedApps.push(authorizedIntake) : this.authorizedApps.push();

        const policy = {
            userEmail: this.backOfficeRoleForm.controls["email"].value,
            userFullName: this.backOfficeRoleForm.controls["name"].value,
            userCountry: this.backOfficeRoleForm.controls["country"].value,
            userRole: Number(this.backOfficeRoleForm.controls["role"].value),
            authorizedApps: this.authorizedApps,
            createdDate: new Date().toString(),
            updatedDate: new Date().toString()
        };

        if (this.validateForm()) {
            this.modalRef.hide();
            await this.firestoreService.updateDocument<PolicyModel>(policy, environment.firebaseConfig.policyCollection, this.data.id);
        }
    }

    validateForm(): boolean {
        this.backOfficeRoleForm.controls["email"].markAsTouched();
        this.backOfficeRoleForm.controls["name"].markAsTouched();
        this.backOfficeRoleForm.controls["country"].markAsTouched();
        this.backOfficeRoleForm.controls["role"].markAsTouched();

        return this.backOfficeRoleForm.valid;
    }
}
